// GWP Threshold Banner - Simple Logic
// Shows "Eligible for FREE X-piece gift" message based on price & purchase type
// Settings from Shopify Admin > Theme Settings > GWP Threshold Settings

(function () {
  // Get the banner element and check if GWP is enabled
  const box = document.getElementById('eligible-free-box');
  if (!box) return;

  const settings = globalThis.GWP_SETTINGS || {};
  const isEnabled = settings.enabled !== false;
  const isKcp = box.dataset.kcp === 'true';

  // KCP discount percent (read from the global KCP config element injected in theme.liquid)
  const kcpDiscount = (function () {
    if (!isKcp) return 0;
    const el = document.getElementById('algolia-kcp-config');
    return el ? (Number.parseInt(el.dataset.discount, 10) || 15) : 15;
  })();

  // Selected variant, captured from the theme's variantChanged event.
  // Avoids reading stale variant JSON while the DOM is still updating.
  let currentVariant = null;

  // HELPER: Resolve the currently selected variant (event data first, JSON fallback)
  function getVariant() {
    if (currentVariant?.id) return currentVariant;
    try {
      const urlVariantId = new URLSearchParams(globalThis.location.search).get('variant');
      const jsonEl = (urlVariantId && document.getElementById('variant-json-' + urlVariantId)) ||
        document.querySelector('script[id^="variant-json-"]');
      return jsonEl ? JSON.parse(jsonEl.textContent) : null;
    } catch (e) {
      console.warn('Error parsing variant JSON:', e);
      return null;
    }
  }

  // HELPER: Get current product price in cents
  function getPrice() {
    const variant = getVariant();
    let price = variant ? Number(variant.price) || 0 : 0;
    // When KCP is active, use the discounted price for threshold comparison
    if (isKcp && price > 0) {
      price = Math.floor(price * (100 - kcpDiscount) / 100);
    }
    return price;
  }

  // HELPER: Check if the current variant has a non-empty custom.eligible_for_free metafield
  function hasEligibleMetafield() {
    const variant = getVariant();
    if (!variant) return false;
    const value = globalThis.GWP_ELIGIBLE_MESSAGES?.[String(variant.id)];
    // Rich-text value is an object; "has value" only if some text node is non-empty
    return /"value":"\s*[^\s"]/.test(JSON.stringify(value || ''));
  }

  // HELPER: Check if subscription is active
  function isSubscribed() {
    const offer = document.querySelector('og-offer[product]');
    return !!offer?.querySelector('og-optin-button[subscribed]');
  }

  // HELPER: Check if gift set is selected
  function isGiftSet() {
    const radio = document.querySelector('input[name="product-subscribe"][value="gift"]');
    return radio?.checked;
  }

  // HELPER: Resolve the eligible-gift message + class for a price/purchase type
  function resolveThreshold(price, isSubscription) {
    // Get threshold settings from admin (in cents)
    const t1 = settings.threshold1Price || 15000;   // $150
    const t2 = settings.threshold2Price || 20000;   // $200
    const t3 = settings.threshold3Price || 30000;   // $300

    // SUBSCRIPTION - Check thresholds against ORIGINAL price (no discount applied)
    if (isSubscription) {
      if (price >= t3) {
        // $300+ → threshold-4
        return { message: settings.threshold3MsgKcp, className: 'threshold-4' };
      }
      if (price >= t2) {
        // $200-299 → threshold-2
        return { message: settings.threshold2Msg, className: 'threshold-2' };
      }
      // No message for subscription < $200
      return { message: null, className: null };
    }

    // ONE-TIME PURCHASE
    if (price >= t3) {
      // $300+ → threshold-3
      return {
        message: isKcp ? settings.threshold3MsgKcp : settings.threshold3MsgNonKcp,
        className: 'threshold-3'
      };
    }
    if (price >= t2) {
      // $200-299 → threshold-2
      return { message: settings.threshold2Msg, className: 'threshold-2' };
    }
    if (price >= t1 && !isKcp) {
      // $150-199 → threshold-1 (non-KCP only)
      return { message: settings.threshold1Msg, className: 'threshold-1' };
    }
    return { message: null, className: null };
  }

  // MAIN LOGIC: Determine what message to show
  function showMessage() {
    // Hide if GWP is disabled or a gift set is selected
    if (!isEnabled || isGiftSet()) {
      box.style.display = 'none';
      return;
    }

    // Skip threshold logic entirely when the variant has no eligible_for_free metafield
    if (!hasEligibleMetafield()) {
      box.style.display = 'none';
      return;
    }

    const result = resolveThreshold(getPrice(), isSubscribed());

    // Display or hide banner
    if (result.message) {
      const text = document.getElementById('eligible-free-text');
      text.innerHTML = '<p style="margin:4px 0">Eligible for ' + result.message + '<br><strong>No code required.</strong></p>';
      box.setAttribute('class', 'eligible-free-box ' + result.className);
      box.style.display = 'flex';
    } else {
      box.style.display = 'none';
    }
  }

  // Listen for changes and update
  let updateTimer;
  function scheduleUpdate() {
    clearTimeout(updateTimer);
    updateTimer = setTimeout(showMessage, 100);
  }

  // Watch for subscription state changes on og-offer element
  const offerElement = document.querySelector('og-offer[product]');
  if (offerElement) {
    const observer = new MutationObserver(function() {
      scheduleUpdate();
    });
    observer.observe(offerElement, { attributes: true, attributeFilter: ['subscribed'] });
  }

  // Watch for variant changes
  const optionsEl = document.querySelector('product-options-element[data-id]');
  if (optionsEl) {
    globalThis.addEventListener('theme:product:' + optionsEl.dataset.id + ':variantChanged', function (event) {
      currentVariant = event?.detail?.variant || null;
      scheduleUpdate();
    });
  }

  // Re-evaluate when product-form.liquid reports a variant change (URL / radio / variant:changed)
  globalThis.addEventListener('gwp:refresh', function (event) {
    const variantId = event?.detail?.variantId;
    if (variantId && String(currentVariant?.id) !== String(variantId)) currentVariant = null;
    scheduleUpdate();
  });

  // Watch for subscription button clicks
  document.addEventListener('click', function(e) {
    if (e.target.closest('og-optin-button') || e.target.closest('og-optout-button')) {
      scheduleUpdate();
    }
  });

  // Watch for gift set selection
  document.addEventListener('change', function(e) {
    if (e.target.name === 'product-subscribe' || e.target.closest('og-select-frequency')) {
      scheduleUpdate();
    }
  });

  // Initial display - delay to let OG element initialize (1000ms for page returns with persisted state)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      setTimeout(showMessage, 1000);
    });
  } else {
    setTimeout(showMessage, 1000);
  }
})();
